import {
  computed, ref, h, onMounted, watch,
} from 'vue';
import CacheManager from '@vue-util/cacheManager';
import { fireTag } from '@vue-util/tagManager';
import useFireTagAsync from '@modules/discovery/composables/useFireTagAsync';
import useFacet from './useFacet';
import useMySizes from './useMySizes';
import useFacets from './useFacets';
import useSortFilter from './useSortFilter';

const dataName = ref('');

export default function useSizeFacet(props) {
  const {
    facet: sizeFacet,
  } = useFacet(props);

  const {
    globalOptions,
    isMobile,
    isMobileInLandscape,
    openedDropdown,
  } = useFacets();

  const {
    transformedSelectedMySizes,
  } = useMySizes();

  const {
    selections,
    rightSideDrawer,
  } = useSortFilter();

  let filteredFacet = ref(sizeFacet.value);
  let dropdownOptions = ref([]);
  const selectedOption = ref('');
  const isReady = ref(false);
  const enableAsyncAnalyticsTags = globalOptions?.consumerStore?.state?.envProps?.enableAsyncAnalyticsTags;

  const toggleState = ref(globalOptions.mySizesData.value?.toggleValue || false);

  const getChildrenValues = ((children) => children.map((item) => item.values).flat());
  /* istanbul ignore else */
  if (sizeFacet.value?.children?.length) {
    const sizeChildren = computed(() => sizeFacet.value.children.map((child) => {
      let selectedChildValues = sizeFacet.value.selectedValues.find((obj) => obj.name === child.name)?.selectedValues || [];
      let childValues = child.values;
      /* istanbul ignore else */
      if (child.children) {
        childValues = getChildrenValues(child.children);
        selectedChildValues = childValues.flatMap((val) => (val.selected ? [val.value] : []));
      }

      let selectedMySizesValues = [];
      /* istanbul ignore else */
      if (toggleState.value) {
        const selectedMySizes = childValues.filter((item) => item.personal);
        selectedMySizesValues = selectedMySizes.map((size) => size.value);
      }

      const selectedValues = [...new Set([...selectedChildValues, ...selectedMySizesValues])];

      return {
        name: child.displayName,
        altName: child.name,
        facetType: child.facetType,
        selectedValues,
        values: childValues,
        children: child.children,
        parent: sizeFacet.value.name,
      };
    }));

    dropdownOptions = computed(() => sizeChildren.value.map((child) => ({ value: child.name, displayValue: child.name })));

    const allSelectedSizeValues = computed(() => sizeChildren.value.flatMap((child) => child.selectedValues));
    /* istanbul ignore else */
    if (dropdownOptions.value.length > 1) {
      dropdownOptions.value.unshift({ value: 'Select Size Range', displayValue: 'Select Size Range' });
    }

    const sessionValue = CacheManager.getSession('selectedSizeOption') || '';

    const previousSelectedOption = computed(() => dropdownOptions.value.filter((item) => item.value === sessionValue));

    let dropdownIndex = 0;
    /* istanbul ignore else */
    if (dropdownOptions.value?.length > 1) {
      dropdownIndex = 1;
    }

    const dropdownValue = dropdownOptions.value[dropdownIndex]?.value;
    selectedOption.value = previousSelectedOption.value.length ? previousSelectedOption.value[0].value : dropdownValue;
    /* istanbul ignore else */
    if (!sessionValue) {
      CacheManager.setSession('selectedSizeOption', selectedOption.value);
    }

    filteredFacet = computed(() => ({
      ...(sizeChildren.value.filter((child) => child.name === selectedOption.value)[0] || {}),
      allSelectedValues: allSelectedSizeValues.value,
    }));
  }

  const onDropdownChange = (e) => {
    selectedOption.value = e.target.value;
    CacheManager.setSession('selectedSizeOption', selectedOption.value);

    if (enableAsyncAnalyticsTags) {
      useFireTagAsync(() => fireTag('link', {
        event_name: 'facet size dropdown click',
        link_name: 'facet size dropdown click',
      }));
    } else {
      fireTag('link', {
        event_name: 'facet size dropdown click',
        link_name: 'facet size dropdown click',
      });
    }
  };

  const sizeValueClick = (e) => {
    dataName.value = e.currentTarget.getAttribute('dataName');
  };

  const customValueRenderer = (value) => {
    /* istanbul ignore else */
    if (value.title) {
      return {
        label: h('div', { class: 'title', innerHTML: `${value.title}` }),
        labelType: 'html',
      };
    }
    const updatedValue = value.originalVal || value.value;
    const result = updatedValue.split(',');
    let div = `<div class='text'>${result[0]}</div>`;
    /* istanbul ignore else */
    if (result.length > 1) {
      div += `<div class='sub-text'>${result[1].trim()}</div>`;
    }

    return {
      label: h('div', {
        class: 'size-value-container', innerHTML: `<div class="item" label="${updatedValue}" title="${updatedValue} (${value.count})">${div}</div>`, dataName: value.dataName, onClick: sizeValueClick,
      }),
      ariaLabel: updatedValue,
      value: value.value,
      labelType: 'html',
    };
  };

  const onUpdate = (selected) => {
    let name = filteredFacet.value.altName;
    let selectedValues = selected;
    /* istanbul ignore else */
    if (sizeFacet.value?.children?.length) {
      /* istanbul ignore else */
      if (filteredFacet.value.children) {
        const child = filteredFacet.value.children.filter((item) => item.name === dataName.value);
        /* istanbul ignore else */
        if (child.length) {
          name = child[0].name;
          const childValues = child[0].values.map((val) => val.value);
          const filtered = selectedValues.filter((val) => childValues.includes(val));
          selectedValues = child[0].values.flatMap((val) => (filtered.includes(val.value) ? [val.originalVal] : []));
        }
      }

      const obj = {
        name,
        selectedValues,
      };

      const foundIndex = sizeFacet.value.selectedValues.findIndex((value) => value.name === obj.name);
      if (foundIndex >= 0) {
        sizeFacet.value.selectedValues[foundIndex] = obj;
      } else {
        sizeFacet.value.selectedValues.push(obj);
      }

      const filteredValues = sizeFacet.value.selectedValues.filter(((item) => item.selectedValues.length));
      sizeFacet.value.selectedValues = filteredValues;
    }
    return selectedValues;
  };

  const updateMySizes = (checked) => {
    const selectionFacets = selections.value.facets.map((facet) => facet);
    const size = selectionFacets.filter((facet) => facet.name === 'Size')[0];
    const facetArray = selectionFacets.filter((facet) => (facet.name !== 'Size' && facet.selectedValues.length));

    const updatedValues = size.children.map((item) => {
      const filteredMySize = transformedSelectedMySizes.value.filter((data) => item.name === data.name)[0] || { selectedValues: [] };
      const filteredSelected = item.values.flatMap((val) => (val.selected ? [val.value] : []));
      let updated;
      if (checked) {
        updated = [...new Set([...filteredSelected, ...filteredMySize.selectedValues])];
      } else {
        updated = filteredSelected.filter((val) => !filteredMySize.selectedValues.includes(val));
      }
      return { name: item.name, selectedValues: updated };
    });
    const selectedValues = updatedValues.filter((item) => item.selectedValues.length);
    size.selectedValues = selectedValues;
    facetArray.push(size);
    globalOptions.onSelectedOption(facetArray);
  };

  const onToggleChange = (e) => {
    const { checked } = e.target;
    const payload = {
      values: [
        {
          value: checked ? 'Y' : 'N',
          selected: true,
        },
      ],
    };
    updateMySizes(checked);
    globalOptions.onSaveMySizesToggle(payload, checked);
  };
  const sizeDropdownExp = computed(() => (rightSideDrawer.value || isMobile.value || isMobileInLandscape.value ? '' : 'size-dropdown-full-width'));

  onMounted(() => {
    isReady.value = true;
  });

  // reset size session on a desktop
  watch(openedDropdown, () => {
    /* istanbul ignore else */
    if (!rightSideDrawer.value) {
      CacheManager.removeSession('selectedSizeOption');
    }
  });

  return {
    dropdownOptions,
    selectedOption,
    filteredFacet,
    onDropdownChange,
    customValueRenderer,
    onUpdate,
    sizeFacet,
    onToggleChange,
    toggleState,
    updateMySizes,
    dataName,
    sizeValueClick,
    sizeDropdownExp,
    isReady,
  };
}
